import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Layout, FileText, Image, LogOut, Users, BarChart2 } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';

const Dashboard = () => {
  const [stats, setStats] = useState({
    blogs: 0,
    portfolios: 0,
    publishedBlogs: 0,
    portfolioImages: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        
        // Get blogs count
        const { count: blogsCount, error: blogsError } = await supabase
          .from('blogs')
          .select('*', { count: 'exact', head: true });
          
        if (blogsError) throw blogsError;
        
        // Get published blogs count
        const { count: publishedBlogsCount, error: publishedBlogsError } = await supabase
          .from('blogs')
          .select('*', { count: 'exact', head: true })
          .eq('published', true);
          
        if (publishedBlogsError) throw publishedBlogsError;
        
        // Get portfolios count
        const { count: portfoliosCount, error: portfoliosError } = await supabase
          .from('portfolios')
          .select('*', { count: 'exact', head: true });
          
        if (portfoliosError) throw portfoliosError;
        
        // Get portfolio images count
        const { count: portfolioImagesCount, error: portfolioImagesError } = await supabase
          .from('portfolio_images')
          .select('*', { count: 'exact', head: true });
          
        if (portfolioImagesError) throw portfolioImagesError;
        
        setStats({
          blogs: blogsCount || 0,
          portfolios: portfoliosCount || 0,
          publishedBlogs: publishedBlogsCount || 0,
          portfolioImages: portfolioImagesCount || 0,
        });
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = '/admin/login';
  };

  return (
    <AdminLayout title="Dashboard" subtitle="Overview of your website content">
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-teal-400 border-t-transparent rounded-full"></div>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="bg-gray-800 border-gray-700 shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-gray-200 flex items-center">
                  <FileText className="mr-2 h-5 w-5 text-teal-400" />
                  Total Blogs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">{stats.blogs}</div>
                <p className="text-sm text-gray-400 mt-1">{stats.publishedBlogs} published</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gray-800 border-gray-700 shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-gray-200 flex items-center">
                  <Layout className="mr-2 h-5 w-5 text-teal-400" />
                  Total Portfolios
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">{stats.portfolios}</div>
                <p className="text-sm text-gray-400 mt-1">Client projects</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gray-800 border-gray-700 shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-gray-200 flex items-center">
                  <Image className="mr-2 h-5 w-5 text-teal-400" />
                  Portfolio Images
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">{stats.portfolioImages}</div>
                <p className="text-sm text-gray-400 mt-1">Across all portfolios</p>
              </CardContent>
            </Card>
            
            <Card className="bg-gray-800 border-gray-700 shadow-md">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-gray-200 flex items-center">
                  <Users className="mr-2 h-5 w-5 text-teal-400" />
                  Admin Users
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-white">1</div>
                <p className="text-sm text-gray-400 mt-1">Active administrators</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Quick Actions */}
          <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <Card className="bg-gray-800 border-gray-700 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg text-gray-200">Manage Blog Posts</CardTitle>
                <CardDescription className="text-gray-400">
                  Create, edit, and delete blog posts
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-300">
                  {stats.blogs} total posts ({stats.publishedBlogs} published)
                </p>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button asChild variant="ghost" className="text-teal-400 hover:text-teal-300 hover:bg-gray-700">
                  <Link to="/admin/blogs">View All</Link>
                </Button>
                <Button asChild className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600">
                  <Link to="/admin/blogs/new">New Post</Link>
                </Button>
              </CardFooter>
            </Card>
            
            <Card className="bg-gray-800 border-gray-700 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg text-gray-200">Manage Portfolios</CardTitle>
                <CardDescription className="text-gray-400">
                  Create, edit, and delete portfolio projects
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-300">
                  {stats.portfolios} total projects with {stats.portfolioImages} images
                </p>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button asChild variant="ghost" className="text-teal-400 hover:text-teal-300 hover:bg-gray-700">
                  <Link to="/admin/portfolios">View All</Link>
                </Button>
                <Button asChild className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600">
                  <Link to="/admin/portfolios/new">New Project</Link>
                </Button>
              </CardFooter>
            </Card>
            
            <Card className="bg-gray-800 border-gray-700 shadow-md">
              <CardHeader>
                <CardTitle className="text-lg text-gray-200">Visit Website</CardTitle>
                <CardDescription className="text-gray-400">
                  View your public-facing website
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-300">
                  See how your changes appear to visitors
                </p>
              </CardContent>
              <CardFooter className="flex justify-between">
                <Button asChild variant="ghost" className="text-teal-400 hover:text-teal-300 hover:bg-gray-700">
                  <a href="/" target="_blank" rel="noopener noreferrer">Open Website</a>
                </Button>
                <Button variant="destructive" onClick={handleSignOut}>
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </CardFooter>
            </Card>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default Dashboard; 